import React from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { MotifProgressLoader,MotifButton} from "@ey-xd/motif-react";
import { apiQueryWithToken } from "../../common/services/apiBaseUrlConfig";
import { getAccount } from "../../common/services/commonService";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { getLoginDetail } from "../../common/redux/common/login/loginSlice";
import { useDispatch } from "react-redux";
import useLogin from "../../common/redux/common/login/useLogin";
import LOCALES from "../../common/locales";
import { ADMIN, PARTNER, USER } from "../../common/contants/constants";
import { ApiStatus } from "../../common/services/api-status.enum";
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';

import "./Login.scss";
import logger from "../../common/utils/logger";
import SafeHtml from "../../common/SafeHtml";

const LOGIN_ATTEMPTED_KEY = "loginAttempted";
const LOGIN_ATTEMPTED_AT_KEY = "loginAttemptedAt";
const LOGIN_ATTEMPT_TTL_MS = 15000;

const clearLoginAttempt = () => {
  sessionStorage.removeItem(LOGIN_ATTEMPTED_KEY);
  sessionStorage.removeItem(LOGIN_ATTEMPTED_AT_KEY);
};

const hasRecentLoginAttempt = () => {
  if (sessionStorage.getItem(LOGIN_ATTEMPTED_KEY) !== "true") {
    return false;
  }

  const attemptedAt = Number(sessionStorage.getItem(LOGIN_ATTEMPTED_AT_KEY) || "0");
  if (!attemptedAt || Date.now() - attemptedAt > LOGIN_ATTEMPT_TTL_MS) {
    clearLoginAttempt();
    return false;
  }

  return true;
};

const hasPendingAuthResponse = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const { search = "", hash = "" } = window.location;
  const authResponsePattern = /(?:^|[?#&])(code|state|error|error_description)=/i;
  return authResponsePattern.test(search) || authResponsePattern.test(hash);
};

export const Login = () => {
  const [role, setRole] = React.useState("");
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = getAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAcknowledgment, setShowAcknowledgment] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showRestrictedDialog, setShowRestrictedDialog] = useState(false); // New state for restricted dialog
  const [showTokenExpiryDialog, setShowTokenExpiryDialog] = useState(false); // New state for restricted dialog
  const { loginDetail } = useLogin();
  const [searchParams] = useSearchParams();
  const isEPUser = loginDetail?.data?.isEP === true;
  const isEngagementAdminUser = loginDetail?.data?.isEngagementAdmin === true;
  // useEffect(() => {
  //   const acknowledgmentShown = sessionStorage.getItem('acknowledgmentShown');
  //   if (!acknowledgmentShown) {
  //     setShowAcknowledgment(true);
  //   }
  // }, []);

  const handleAcknowledge = () => {
   // sessionStorage.setItem('acknowledgmentShown', 'true');
    apiQueryWithToken
        .post(`/User/update/Acknowledged`, 
          account?.username
        )
        .then((role) => {
          setShowAcknowledgment(false);
          window.location.reload();
        })
        .catch((error) => {
          logger.error(error);
        });
   
    
  };
  const getLoginDetails = React.useCallback(async () => {
    if (isAuthenticated) {
      clearLoginAttempt();
      return;
    }

    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (hasPendingAuthResponse() || hasRecentLoginAttempt()) {
      return;
    }

    sessionStorage.setItem(LOGIN_ATTEMPTED_KEY, "true");
    sessionStorage.setItem(LOGIN_ATTEMPTED_AT_KEY, String(Date.now()));
    try {
      await instance.loginRedirect({
        scopes: ["user.read"],
      });
    } catch (error) {
      if (error?.errorCode === "interaction_in_progress") {
        console.warn(
          "Another interaction is currently in progress. Please wait."
        );
        return;
      }
      clearLoginAttempt();
      console.error(error);
    }
  }, [isAuthenticated, inProgress, instance]);


  useEffect(() => {
    getLoginDetails();
  }, [getLoginDetails]);
  
  const dispatch = useDispatch();
  useEffect(() => {
    const payload = {
      userName: account?.name,
      emailId: account?.username,
    };
    if(account?.username) {
      dispatch(getLoginDetail(payload));
    }
  }, [dispatch, account?.name, account?.username]);


  useEffect(() => {
    if (loginDetail?.status === ApiStatus.SUCCESS) {
      // Check for eng_guid in current URL's searchParams or from the original location
      let engID = searchParams.get('eng_guid') || new URLSearchParams(location.state?.from?.search).get('eng_guid');

      // If eng_guid is not found directly, check if it's encoded within another parameter
      if (!engID) {
        const fullSearchString = decodeURIComponent(searchParams.toString());
        const engGuidMatch = fullSearchString.match(/eng_guid=([^&]+)/);
        if (engGuidMatch) {
          engID = engGuidMatch[1];
        }
      }

      // Check if user came from FAQ
      const viewFAQ = searchParams.get('viewfaq') || new URLSearchParams(location.state?.from?.search).get('viewfaq')
      const cameFromFAQViewer = location.state?.from?.pathname === '/faq-viewer';
      const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
      if (postLoginRedirect) {
        sessionStorage.removeItem('postLoginRedirect');
      }

      if (viewFAQ || cameFromFAQViewer) {
        navigate('/faq-viewer');
      } else if (engID) {
        navigate('/uploadedFiles?tab=dataUpload&eng_guid=' + engID);
      } else if (postLoginRedirect && postLoginRedirect !== "/") {
        navigate(postLoginRedirect);
      }

     setShowAcknowledgment(loginDetail?.data?.isAcknowledged === false);
      setRole(
        loginDetail?.data?.userRole === ""
          ? "User doesn't exsist"
          : loginDetail?.data?.userRole
      );
      // profileDetails is a UI cache only. The backend re-validates every API call
      // via the MSAL bearer token. UI elements derived from these flags (e.g. isEP,
      // isEngagementAdmin) must not be treated as a security boundary.
      sessionStorage.setItem(
        "profileDetails",
        JSON.stringify(loginDetail?.data)
      );
      if (
        loginDetail?.data?.userRole === "User doesn't exsist" ||
        loginDetail?.data?.userRole === ""
      ) {
        setShowRestrictedDialog(true); // Show restricted dialog if role is User
      } else {
        setShowRestrictedDialog(false);
      }
      setShowTokenExpiryDialog(false);
    }
     if(loginDetail?.status === ApiStatus.ERROR) {
      setRole(USER);
      //setShowTokenExpiryDialog(true); // Show token expiry dialog on 401 error
    }
  }, [loginDetail, loginDetail?.data, loginDetail?.status]);

  const customText = role === PARTNER ? 'Welcome to EY-P DBS\nData Submission Portal' : 'The Data Bureau Service';
  const customClass = role === PARTNER ? 'section_login-partner' : 'section_login';

  return (
    <div className="Login">
      {role === "" ? (
        <MotifProgressLoader show varian="default" />
      ) : (
        <>
          <div className="login-right">
            <div className="login-right-wrapper">
              <div className={`section_login ${customClass}`}>
                <p style={{ whiteSpace: 'pre-line' }}>{customText}</p>
                <div
                  className={
                    role === PARTNER ? "description-partner" : "description"
                  }
                >
                  {role === PARTNER ? (
                    <>
                      <div className="partner-paragraph">
                        {LOCALES.HOME.PARTNER.TITLE}
                      </div>
                      <div>
                        {LOCALES.HOME.PARTNER.SUB_TITLE}
                      </div>
                    </>
                  ) : (
                    role !== USER && (
                      <>
                        {LOCALES.HOME.ADMIN.TITLE}
                      </>
                    )
                  )}
                </div>
                <div className="user_type">
                  {role === ADMIN && (
                    <div className="lft">
                      <span className="pdright">
                        <a
                          href=""
                          className="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate("/adminPage");
                          }}
                        >
                          <img
                            className="noHover"
                            src={require("../../../src/common/images/user.png")}
                            alt="user"
                          />
                          <img
                            className="hoverEffect"
                            src={require("../../../src/common/images/users_confirm.png")}
                            alt="users"
                          />
                        </a>
                      </span>
                      <p className="label">{LOCALES.LABEL.DBS_USER}</p>
                    </div>
                  )}
                  {role === PARTNER && (isEPUser || isEngagementAdminUser) && (
                    <div className="lft">
                      <span>
                        <a
                          href=""
                          className="icon self-service-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/uploadedFiles?tab=engagementQualification`);
                          }}
                        >
                          <img
                            className="noHover self-service-icon"
                            src={require("../../../src/common/images/self_service_icon.png")}
                            alt="self-service"
                          />
                          <img
                            className="hoverEffect self-service-icon"
                            src={require("../../../src/common/images/self_service_icon.png")}
                            alt="self-service"
                          />
                        </a>
                      </span>
                      <p className="label">EY-P DBS Engagement Qualification</p>
                    </div>
                  )}
                  {(role === ADMIN || role === PARTNER) && (
                    <div className="rght">
                      <span>
                        <a
                          href=""
                          className="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            {
                              role === PARTNER
                                ? navigate(`/uploadedFiles?tab=dataUpload`)
                                : navigate(`/adminPage`);
                            }
                          }}
                        >
                          <img
                            className="noHover"
                            src={require("../../../src/common/images/verified-user.png")}
                            alt="admin"
                          />
                          <img
                            className="hoverEffect"
                            src={require("../../../src/common/images/verified-user_confirmation.png")}
                            alt="admin"
                          />
                        </a>
                      </span>
                      <p className="label">
                        {role === PARTNER ? "EY-P DBS File Upload" : "EY-P DBS Admin"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Dialog
            open={showAcknowledgment}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setShowAcknowledgment(false);
              }
            }}
            className="acknowledge-dialog"
          >
            <DialogTitle>
              {"Privacy Note – EY-P Data Bureau Service"}
            </DialogTitle>
            <DialogContent>
              <div>
                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_ONE.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_TWO.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_THREE.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_FOUR.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_FIVE.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_SIX.replace(/\n/g, "")}
                  className="ack-table"
                />
                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_SEVEN.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_EIGHT.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_NINE.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_TEN.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_ELEVEN.replace(/\n/g, "")}
                />

                <SafeHtml
                  as="p"
                  html={LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_TWELVE.replace(/\n/g, "")}
                />
              </div>
              <div>
                <input
                  type="checkbox"
                  id="acknowledgeCheckbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <label htmlFor="acknowledgeCheckbox">
                  <strong>{LOCALES.ACKNOWLEDGE_MODAL.ACKNOWLEDGE_LABEL}</strong>
                </label>
              </div>
            </DialogContent>
            <DialogActions className="button_container">
              <MotifButton
                onClick={() => {
                  if (acknowledged) {
                    if (role === USER) {
                      setShowAcknowledgment(false);
                    } else {
                      handleAcknowledge();
                    }
                  }
                }}
                className={acknowledged ? "acknowledge_class" : ""}
                variant={acknowledged ? "primary" : "secondary"}
                disabled={!acknowledged}
              >
                {LOCALES.LABEL.ACKNOWLEDGE}
              </MotifButton>
            </DialogActions>
          </Dialog>
          <Dialog
            open={showRestrictedDialog}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setShowRestrictedDialog(false);
              }
            }}
            className="role-ristrict-dialog"
          >
            <DialogTitle>{"Access Restricted"}</DialogTitle>
            <DialogContent>
              {LOCALES.RESTRICTION.FIRST_MESSAGE} <a href="mailto:DBS@parthenon.ey.com">{LOCALES.RESTRICTION.LINK}</a> {LOCALES.RESTRICTION.SECOND_MESSAGE}
            </DialogContent>
          </Dialog>
          <Dialog
            open={showTokenExpiryDialog}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setShowTokenExpiryDialog(false);
              }
            }}
            className="role-ristrict-dialog"
          >
            <DialogTitle>{"Session Timed Out"}</DialogTitle>
            <DialogContent>
              {LOCALES.MESSAGES.TIMEOUT}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};
